import { useEffect } from "react";
import { Link } from "wouter";
import { PageMeta } from "@/components/PageMeta";
import { CHRISTMAS_PARTY_ARTICLE as ARTICLE } from "@/lib/christmasPartyArticle";

export default function ChristmasPartyArticlePage() {
  const canonical = `https://www.angkorflavours.com.au${ARTICLE.path}`;
  const image = `https://www.angkorflavours.com.au${ARTICLE.image}`;
  useEffect(() => {
    let tag = document.head.querySelector<HTMLMetaElement>(
      'meta[property="og:type"]'
    );
    const created = !tag;
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("property", "og:type");
      document.head.appendChild(tag);
    }
    tag.content = "article";
    return () => {
      if (created) tag?.remove();
      else if (tag) tag.content = "website";
    };
  }, []);
  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${canonical}#article`,
    headline: ARTICLE.headline,
    datePublished: ARTICLE.datePublished,
    dateModified: ARTICLE.datePublished,
    image,
    mainEntityOfPage: canonical,
    author: { "@type": "Organization", name: "ANGKOR team" },
    publisher: { "@type": "Organization", name: "ANGKOR Cambodian Flavours" },
  };
  return (
    <article className="blog-article">
      <PageMeta
        title={ARTICLE.title}
        description={ARTICLE.description}
        path={ARTICLE.path}
        image={image}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
      <section className="intent-hero">
        <div className="container intent-hero-grid">
          <div>
            <Link href="/blog" className="eyebrow blog-back-link">
              Blog
            </Link>
            <h1>{ARTICLE.headline}</h1>
            <p className="blog-byline">
              <time dateTime={ARTICLE.datePublished}>{ARTICLE.dateLabel}</time>{" "}
              · {ARTICLE.byline}
            </p>
          </div>
          <img
            src={ARTICLE.image}
            sizes="(max-width: 767px) calc(100vw - 32px), 50vw"
            alt={ARTICLE.imageAlt}
            width="1800"
            height="1012"
            fetchPriority="high"
            loading="eager"
          />
        </div>
      </section>
      <section className="section parchment-section">
        <div className="container">
          <div className="blog-article-body">
            <p>
              {
                "The end of the year comes around fast. If you are the one organising this year's work Christmas party, team lunch or family get-together, you already know the hard part is not the date, it is finding somewhere that feels special, feeds everyone well and does not turn into a logistics headache. ANGKOR Cambodian Flavours at 389 Spencer Street, West Melbourne, is a warm, intimate restaurant built around shared food, and it works beautifully for end-of-year celebrations."
              }
            </p>
            <h2>{"Why a Cambodian feast suits an end-of-year party"}</h2>
            <p>
              {
                "A Christmas party is about bringing people together, and Cambodian food is made for exactly that. Dishes arrive in the middle of the table, rice is passed around and everyone tries a bit of everything. Nobody has to agonise over a menu, and conversation flows across the table instead of staying stuck at one end."
              }
            </p>
            <p>
              {
                "The flavours are generous without being heavy: fragrant kroeung (a paste of lemongrass, galangal, kaffir lime, turmeric and garlic), coconut, fresh herbs, grilled meats and bright salads. Our signature Fish Amok, a softly steamed coconut curry, is a great talking point for guests who have never tried Khmer food. You can read its story on our "
              }
              <Link href="/fish-amok-melbourne">{"Fish Amok page"}</Link>
              {"."}
            </p>
            <h2>{"Two ways to book your group"}</h2>
            <p>
              <strong>{"A feast for 12 to 24 guests."}</strong>
              {
                " For most office teams and family groups, our Cambodian event feasts are the easy choice. There are two set feasts: the Angkor Feast at $79 per adult and the Royal Khmer Feast at $99 per adult. Event feasts start at a minimum of 12 adults, children aged 5 to 11 have a dedicated price and children under 5 are complimentary. Groups of 12 to 24 can enjoy either feast without hiring the whole restaurant. You can see the sample menus in the "
              }
              <a href="/manus-storage/angkor-feast-menus-FINAL-rev25_0edbed4f.pdf">
                {"feast menus PDF"}
              </a>
              {"."}
            </p>
            <p>
              <strong>{"Exclusive hire for up to 38 guests."}</strong>
              {
                " Want the room to yourselves? Take over the whole restaurant for up to 38 seated guests, with no room-hire fee. You simply reach a food and beverage minimum spend for your service:"
              }
            </p>
            <ul>
              <li>{"Weekday lunch (Tuesday to Friday): $1,500"}</li>
              <li>{"Weekend lunch (Saturday and Sunday): $2,000"}</li>
              <li>{"Weeknight dinner (Tuesday to Thursday): $2,500"}</li>
              <li>{"Sunday dinner: $2,750 (a 20% Sunday surcharge applies)"}</li>
              <li>{"Friday and Saturday dinner: $3,500"}</li>
            </ul>
            <p>
              {
                "Dinner hire runs for four hours and lunch hire for three, with 60 minutes of set-up access beforehand. Monday private hire is available on request. Public holidays are closed except for exclusive hire, with a 50% surcharge on the minimum spend and menu."
              }
            </p>
            <h2>{"Drinks for the celebration"}</h2>
            <p>
              {
                "Drinks packages apply to all adult guests and are served under Responsible Service of Alcohol:"
              }
            </p>
            <ul>
              <li>
                <strong>{"Soft"}</strong>
                {
                  ", $20 per person for three hours: soft drinks, sparkling water, juices and Lemongrass Ginger Iced Tea."
                }
              </li>
              <li>
                <strong>{"Classic"}</strong>
                {
                  ", $49, $59 or $69 per person for two, three or four hours: beer, house sparkling, white, rosé and red, plus soft drinks."
                }
              </li>
              <li>
                <strong>{"Royal"}</strong>
                {
                  ", $69, $79 or $89 per person for two, three or four hours: the Classic package plus premium wines, bottled beers and cider, and an Angkor Tropical on arrival."
                }
              </li>
            </ul>
            <p>
              {
                "Prefer flexibility? A bar tab lets you set a limit, or guests can buy their own drinks."
              }
            </p>
            <h2>{"Little touches that make it feel like Christmas"}</h2>
            <p>
              {
                "You can add welcome canapés ($10 per person), a welcome sparkling or Angkor Tropical cocktail ($14 per person), or an Angkor Lemon Sunset Soda mocktail ($9 per person). If someone is bringing a celebration cake, we can store, cut and serve it ($3 per person, or $7 with coconut ice cream). For a more festive look there is white linen from $250, black and gold styling with candles and fresh flowers from $375, and personalised printed menus at $3 per person."
              }
            </p>
            <h2>{"Easy for city teams to reach"}</h2>
            <p>
              {
                "ANGKOR sits on Spencer Street in West Melbourne, a short walk from Southern Cross Station and Marvel Stadium, so it is simple for teams coming from the CBD or Docklands and for guests heading home by train. If your party is on a game or concert night, see our guide to "
              }
              <Link href="/restaurant-near-marvel-stadium-southern-cross">
                {"dining near Marvel Stadium and Southern Cross"}
              </Link>
              {"."}
            </p>
            <h2>{"Dietary needs"}</h2>
            <p>
              {
                "Vegetarian, vegan and gluten-free alternatives are available at no extra cost with 7 days' notice, which makes planning for a mixed team much easier. Our kitchen uses peanuts, sesame, gluten, milk, fish, shellfish and molluscs, egg, soy and coconut, so we cannot guarantee an allergen-free meal. Please tell us about every allergy when you book. Our "
              }
              <Link href="/blog/vegetarian-cambodian-food-melbourne">
                {"vegetarian Cambodian food guide"}
              </Link>
              {" shows the kind of plant-friendly dishes we cook."}
            </p>
            <h2>{"How booking works"}</h2>
            <ol>
              <li>
                <strong>{"Enquire."}</strong>
                {" Share your preferred date, guest numbers and occasion."}
              </li>
              <li>
                <strong>{"Confirm."}</strong>
                {
                  " A 50% deposit of the estimated package total, or of the minimum spend for exclusive hire, secures your date."
                }
              </li>
              <li>
                <strong>{"Finalise."}</strong>
                {
                  " Final numbers, menu, dietary needs and balance are due 7 days before."
                }
              </li>
              <li>
                <strong>{"Celebrate."}</strong>
                {" We prepare the table so you can enjoy the day."}
              </li>
            </ol>
            <p>
              {
                "December dates are the first to go, so it is worth locking yours in early. Full details are in our "
              }
              <a href="/manus-storage/angkor-events-packages-v3_939d541c.pdf">
                {"event package"}
              </a>
              {" and on the "}
              <Link href="/groups-functions">{"Functions & Groups page"}</Link>
              {"."}
            </p>
            <h2>{"Opening hours and location"}</h2>
            <p>
              <strong>{"ANGKOR Cambodian Flavours"}</strong>
              <br />
              {"389 Spencer Street, West Melbourne VIC 3003"}
              <br />
              {"Phone: (03) 7034 8353 · info@angkorflavours.com.au"}
            </p>
            <ul>
              <li>{"Monday: closed (private hire on request)"}</li>
              <li>
                {"Tuesday to Thursday: 11:30am to 3:00pm and 4:30pm to 9:30pm"}
              </li>
              <li>{"Friday: 11:30am to 9:30pm"}</li>
              <li>{"Saturday: 12:00pm to 9:30pm"}</li>
              <li>{"Sunday: 4:30pm to 9:30pm"}</li>
            </ul>
            <p>
              <Link href="/location">{"Directions and full hours"}</Link>
            </p>
            <h2>{"Plan your end-of-year party"}</h2>
            <p>
              {
                "Tell us your date and numbers and we will help you choose the feast, drinks and format that fits your group. For smaller tables of under 12, simply book a regular table and try one of our restaurant banquets from the "
              }
              <Link href="/menu">{"dinner menu"}</Link>
              {"."}
            </p>
            <p>
              <a href="/groups-functions#enquire">{"Check Your Date"}</a>
              {"  ·  "}
              <a href="https://reserve.oddle.me/en_AU/angkorcafe">
                {"Book a Table"}
              </a>
              {"  ·  Call (03) 7034 8353"}
            </p>
          </div>
        </div>
      </section>
    </article>
  );
}
